import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import {
  encodeAbiParameters,
  getAddress,
  keccak256,
  type AbiParameter,
  type Address,
  type Hex,
} from 'viem';
import { z } from 'zod';

const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .transform(value => getAddress(value).toLowerCase() as Address)
  .refine(value => value !== `0x${'0'.repeat(40)}`);
const hashSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .transform(value => value.toLowerCase() as Hex)
  .refine(value => value !== `0x${'0'.repeat(64)}`);
const pinSchema = z.strictObject({ account: addressSchema, codeHash: hashSchema });
const assetSchema = z.strictObject({
  account: addressSchema,
  codeHash: hashSchema,
  decimals: z.number().int().min(0).max(255),
});
const commonRoute = {
  router: pinSchema,
  permit2: pinSchema,
  factory: pinSchema,
  poolInitCodeHash: hashSchema,
  tokens: z.array(assetSchema).min(2).max(8),
  pools: z.array(pinSchema).min(1).max(7),
};
const routeSchema = z.discriminatedUnion('family', [
  z.strictObject({ family: z.literal(2), ...commonRoute, fees: z.array(z.never()).length(0) }),
  z.strictObject({
    family: z.literal(3),
    ...commonRoute,
    fees: z.array(z.number().int().min(1).max(999_999)).min(1).max(7),
  }),
]).superRefine((route, context) => {
  if (route.pools.length !== route.tokens.length - 1
    || route.family === 3 && route.fees.length !== route.pools.length) {
    context.addIssue({ code: 'custom', message: 'route lengths mismatch' });
  }
  const roles = [route.router.account, route.permit2.account, route.factory.account,
    ...route.tokens.map(token => token.account), ...route.pools.map(pool => pool.account)];
  if (new Set(roles).size !== roles.length) {
    context.addIssue({ code: 'custom', message: 'route contains a role collision' });
  }
});
const identitySchema = z.strictObject({
  chainId: z.literal(4663),
  engine: addressSchema,
  vault: addressSchema,
  route: routeSchema,
});

export const externalFixedRouteIdentityParameters = [
  { name: 'chainId', type: 'uint256' },
  { name: 'engine', type: 'address' },
  { name: 'vault', type: 'address' },
  {
    name: 'route',
    type: 'tuple',
    components: [
      { name: 'family', type: 'uint8' },
      { name: 'router', type: 'tuple', components: [
        { name: 'account', type: 'address' }, { name: 'codeHash', type: 'bytes32' },
      ] },
      { name: 'permit2', type: 'tuple', components: [
        { name: 'account', type: 'address' }, { name: 'codeHash', type: 'bytes32' },
      ] },
      { name: 'factory', type: 'tuple', components: [
        { name: 'account', type: 'address' }, { name: 'codeHash', type: 'bytes32' },
      ] },
      { name: 'poolInitCodeHash', type: 'bytes32' },
      { name: 'tokens', type: 'tuple[]', components: [
        { name: 'account', type: 'address' }, { name: 'codeHash', type: 'bytes32' },
        { name: 'decimals', type: 'uint8' },
      ] },
      { name: 'pools', type: 'tuple[]', components: [
        { name: 'account', type: 'address' }, { name: 'codeHash', type: 'bytes32' },
      ] },
      { name: 'fees', type: 'uint24[]' },
    ],
  },
] as const satisfies readonly AbiParameter[];

export type ExternalFixedRouteIdentityInput<ChainId extends 4663 | 46630 = 4663> =
  Omit<z.input<typeof identitySchema>, 'chainId'> & { chainId: ChainId };
export type SelectedExternalFixedRouteIdentityInput = ExternalFixedRouteIdentityInput<typeof SELECTED_ACCOUNT_CHAIN_ID>;
export type ExternalFixedRoute = z.output<typeof routeSchema>;

function fixedRouteIdentityCodec<const ChainId extends 4663 | 46630>(chainId: ChainId) {
  const domainSchema = identitySchema.extend({ chainId: z.literal(chainId) });
  /**
   * Builds the byte-exact identity used by ExternalFixedRouteProbe.identityHash.
   * This validates immutable route shape only. It does not observe runtime code,
   * establish route provenance, authorize an order, sign, or submit anything.
   */
  function encodeExternalFixedRouteIdentity(input: ExternalFixedRouteIdentityInput<ChainId>) {
    const canonical = domainSchema.parse(structuredClone(input));
    const encoded = encodeAbiParameters(externalFixedRouteIdentityParameters, [
      BigInt(canonical.chainId), canonical.engine, canonical.vault, canonical.route,
    ]);
    return {
      encoded,
      identityHash: keccak256(encoded),
      chainId: canonical.chainId,
      engine: canonical.engine,
      vault: canonical.vault,
      canonicalRoute: canonical.route,
      signingAuthorized: false as const,
      executionAuthorized: false as const,
    };
  }

  /** The fee-scoped domain is used only by the new engine runtime. Omit
   * feeBps solely when reconstructing a saved legacy route/order. This hash is
   * structural, not an authorization or live-chain observation.
   */
  function deriveExternalFixedRouteId(input: ExternalFixedRouteIdentityInput<ChainId>, feeBps?: number) {
    const identity = encodeExternalFixedRouteIdentity(input);
    const fee = feeBps === undefined ? undefined : z.number().int().min(0).max(9_999).parse(feeBps);
    const parameters = [
      { type: 'uint256' }, { type: 'address' }, { type: 'address' },
      { type: 'address' }, { type: 'address' },
      ...(fee === undefined ? [] : [{ type: 'uint16' }]),
    ] as AbiParameter[];
    const route = identity.canonicalRoute;
    const values = [BigInt(identity.chainId), identity.engine, identity.vault,
      route.tokens[0]!.account, route.tokens.at(-1)!.account,
      ...(fee === undefined ? [] : [fee])];
    return keccak256(encodeAbiParameters(parameters, values));
  }
  return { encodeExternalFixedRouteIdentity, deriveExternalFixedRouteId };
}

export const { encodeExternalFixedRouteIdentity, deriveExternalFixedRouteId } = fixedRouteIdentityCodec(4663);
const selectedIdentity = fixedRouteIdentityCodec(SELECTED_ACCOUNT_CHAIN_ID);
export const encodeSelectedExternalFixedRouteIdentity = selectedIdentity.encodeExternalFixedRouteIdentity;
export const deriveSelectedExternalFixedRouteId = selectedIdentity.deriveExternalFixedRouteId;
